import {
    BaseOneServiceI18n,
    OneServiceI18nOptions,
} from './BaseOneServiceI18n';
import {
    Key,
    listen,
    storage,
    storageEvents,
} from '@soluto-private/mx-context';
import { LOCALES, LocaleCode, LocaleLanguage } from './partner-languages';

import { I18nConfiguration } from '@soluto-private/mx-types';
import { TFunction } from 'i18next';
import { getI18nConfiguration } from './provider';

export class OneServiceI18n extends BaseOneServiceI18n {
    private _i18nConfig: I18nConfiguration;
    private async getConfigurationInstance(
        forceRefresh: boolean = false
    ): Promise<I18nConfiguration> {
        if (!this._i18nConfig || forceRefresh) {
            this._i18nConfig = await getI18nConfiguration();
        }

        return this._i18nConfig;
    }

    public init(opts: OneServiceI18nOptions): Promise<TFunction> {
        this.initializeLocale();
        this.registerToContextEvents();
        return super.init(opts);
    }

    private async initializeLocale() {
        const browserLocale = navigator.language as LocaleCode;
        const { supportedLocales, defaultLocale } =
            await this.getConfigurationInstance(true);
        const currentLocale = this.getLocale() ?? defaultLocale;

        if (supportedLocales.includes(currentLocale)) {
            storage.set(Key.Language, currentLocale);
        } else if (supportedLocales.includes(browserLocale)) {
            storage.set(Key.Language, browserLocale);
        } else {
            storage.set(Key.Language, defaultLocale);
        }
    }

    private registerToContextEvents() {
        listen(storageEvents.keyUpdated(Key.Client), async () => {
            await this.initializeLocale();
        });

        listen(storageEvents.keyUpdated(Key.Language), () => {
            void this.client.changeLanguage();
        });
    }

    public getLocale(): LocaleCode | undefined {
        return storage.get(Key.Language) as LocaleCode;
    }

    public async getSupportedLanguages(): Promise<LocaleLanguage[]> {
        const { supportedLocales } = await this.getConfigurationInstance();
        return supportedLocales
            .map((l) => this.localeToLanguage(l as LocaleCode))
            .filter((locale) => locale);
    }

    public localeToLanguage(code: LocaleCode): LocaleLanguage {
        const [displayLang, lang] = LOCALES[code];
        return { code, lang, displayLang };
    }

    public async setLocale(code: LocaleCode) {
        const { supportedLocales } = await this.getConfigurationInstance();
        if (supportedLocales.includes(code)) {
            storage.set(Key.Language, code);
        }
    }
}
